"use client";

import { useCallback, useMemo } from "react";
import useSWR from "swr";
import type { MapLayer } from "@/lib/types";

export const initialMapData = {
  layers: [] as MapLayer[],
};

type MapData = typeof initialMapData;

/**
 * Helper to create a unique key based on layer content.
 * Prevents multiple "Emerson BBox" entries if the AI loops.
 */
const getLayerFingerprint = (layer: MapLayer): string => {
  try {
    const spec = layer.specification.layers[0];
    if (!spec) return layer.id;
    // Create a key based on the tool used and the parameters (e.g., coordinates)
    return `${spec.source.tool}-${JSON.stringify(spec.source.params)}`;
  } catch (e) {
    return layer.id;
  }
};

export function useMap() {
  const { data: localMapData, mutate: setLocalMapData } = useSWR<MapData>(
    "map-data",
    null,
    { fallbackData: initialMapData }
  );

  const mapData = useMemo(() => {
    return localMapData || initialMapData;
  }, [localMapData]);

  const addLayers = useCallback(
    (newLayers: MapLayer[]) => {
      setLocalMapData((current) => {
        const currentData = current || initialMapData;

        // Use a Map to track layers by their content fingerprint
        // We initialize it with existing layers
        const fingerprintMap = new Map<string, MapLayer>();
        
        currentData.layers.forEach(l => {
          fingerprintMap.set(getLayerFingerprint(l), l);
        });

        // Merge new layers
        newLayers.forEach((layer) => {
          const fingerprint = getLayerFingerprint(layer);
          
          // If this content already exists, we overwrite it (updating the data)
          // but preserve the visibility if it was already set by the user
          const existing = fingerprintMap.get(fingerprint);
          
          fingerprintMap.set(fingerprint, {
            ...layer,
            visible: existing ? existing.visible : (layer.visible ?? true),
          });
        });

        return {
          layers: Array.from(fingerprintMap.values()),
        };
      });
    },
    [setLocalMapData]
  );

  const addLayer = useCallback(
    (layer: MapLayer) => addLayers([layer]),
    [addLayers]
  );

  const clearLayers = useCallback(() => {
    setLocalMapData(initialMapData);
  }, [setLocalMapData]);

  const toggleLayer = useCallback(
    (layerId: string) => {
      setLocalMapData((current) => {
        const currentData = current || initialMapData;
        return {
          ...currentData,
          layers: currentData.layers.map((layer) =>
            layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
          ),
        };
      });
    },
    [setLocalMapData]
  );

  const removeLayer = useCallback(
    (layerId: string) => {
      setLocalMapData((current) => {
        const currentData = current || initialMapData;
        return {
          layers: currentData.layers.filter((l) => l.id !== layerId),
        };
      });
    },
    [setLocalMapData]
  );

  return useMemo(
    () => ({
      layers: mapData.layers,
      addLayer,
      addLayers,
      clearLayers,
      toggleLayer,
      removeLayer,
    }),
    [mapData.layers, addLayer, addLayers, clearLayers, toggleLayer, removeLayer]
  );
}
